import React, { useMemo, useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const MultiSelectField = ({ label, options, selectedValues, onToggle, placeholder }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;

    return (options || []).filter((opt) =>
      String(opt).toLowerCase().includes(q)
    );
  }, [options, query]);

  const openAndFocus = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleToggle = (option) => {
    onToggle(option);
    setQuery('');
    openAndFocus();
  };

  return (
    <div className="flex flex-col gap-1 w-full relative" ref={containerRef}>
      <label className="text-sm text-darkGray">{label}</label>

      <div
        onClick={openAndFocus}
        className="min-h-10 px-3 py-1 bg-white border border-secondary rounded-xl flex flex-wrap items-center gap-2 cursor-text"
      >
        {selectedValues.length > 0 && selectedValues.map((val) => (
          <span
            key={val}
            className="bg-secondary text-darkGray text-xs px-2 py-1 rounded-lg flex items-center gap-1"
          >
            {val}
            <FiX
              onClick={(e) => {
                e.stopPropagation();
                onToggle(val);
              }}
            />
          </span>
        ))}

        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedValues.length === 0 ? placeholder : ""}

          className={[
            "h-8 outline-none text-[13.5px] bg-transparent text-mediumGray placeholder:text-mediumGray",
            selectedValues.length === 0
              ? "flex-1 min-w-[120px] px-1"
              : (query ? "flex-1 min-w-[120px] px-1" : "w-0 p-0"),
          ].join(" ")}
        />

        <FiChevronDown
          className="ml-auto text-darkGray cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((v) => !v);
            if (!isOpen) setTimeout(() => inputRef.current?.focus(), 0);
          }}
        />
      </div>

      {isOpen && (
        <div className="absolute top-[105%] left-0 w-full bg-white border border-secondary rounded-xl shadow-lg z-50">
          <div className="max-h-60 overflow-y-auto rounded-xl">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-darkGray">
                {t('FILTER.NO_RESULTS')}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => handleToggle(option)}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-lightGray cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option)}
                    readOnly
                    className="rounded border-secondary text-secondary focus:ring-secondary"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {option}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectField;